import {
  PersonTeaserData,
  Record,
  NameData,
  AddressData,
  DateOfBirth,
} from '@/search/useSearchPerson/Interfaces';

export function SerializedPersonTeaser(rawData: PersonTeaserData): Record[] {
  const recordResponse = rawData.response.Records?.Record || [];
  const records = Array.isArray(recordResponse)
    ? recordResponse
    : [recordResponse];
  const deadYears = 115;

  const serializedPersonTeaser = records.map((record: Record) => {
    // Names
    const nameData = record.Names?.Name;
    const isNameDataArray = Array.isArray(nameData);
    const nameFormats = nameData
      ? formatFullName(
          isNameDataArray ? nameData[0] : nameData,
          record.hasAge,
          record.age
        )
      : { fullName: '', fullPrefixed: '' };

    const { fullName, fullPrefixed } = nameFormats ?? {
      name: '',
      fullPrefixed: '',
    };
    record.name = fullName;
    record.fullPrefixName = fullPrefixed;
    record.aliases = isNameDataArray
      ? nameData.slice(1).map((name) => formatName(name)) ?? []
      : [];
    record.aliasesNotSerialized = isNameDataArray
      ? nameData.slice(1)
      : nameData;

    delete record.Names;

    // Relatives
    const relativeData = record.Relatives?.Relative;
    const isRelativeDataArray = Array.isArray(relativeData);
    const relativeFormats = isRelativeDataArray
      ? relativeData.map((name) => formatName(name))
      : [formatName(relativeData || {})];

    record.relatives = relativeData ? relativeFormats : [];
    record.relativesNotSerialized = relativeData;

    delete record.Relatives;

    // Addresses
    const addressData = record.Addresses?.Address;
    const isAddressDataArray = Array.isArray(addressData);
    const addressFormats = addressData
      ? isAddressDataArray
        ? addressData.map(formatAddress)
        : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          [formatAddress(addressData!)]
      : [];

    record.locations = addressFormats?.filter((address, index) => {
      return addressFormats.indexOf(address) === index;
    });

    // TODO: Check if we need to check the DateLastSeen to determine the first one
    record.currentLocation = addressFormats[0] || '';
    record.addressNotSerialized = addressData;

    delete record.Addresses;

    // nameMatch
    record.nameMatched = record.exactMatch === '1';
    delete record.exactMatch;

    // BirthInfo / Deceased
    if (record.DOBs) {
      const item = Array.isArray(record.DOBs.Item)
        ? record.DOBs.Item[0]
        : record.DOBs.Item;

      record.deceased = Number(item.Age) > deadYears;
      record.birthInfo = convertToBirthInfo(item);

      delete record.DOBs;
    }

    // Useless properties for now
    delete record.link;
    delete record.hasAge;

    return record;
  });

  return serializedPersonTeaser;
}

const convertToBirthInfo = (item: DateOfBirth) => {
  const { Month, Year } = item.DOB;

  if (!Month) return Year;

  const date = new Date();
  date.setMonth(Number(Month) - 1);

  return `${date.toLocaleString('en-US', {
    month: 'short',
  })} ${Year}`;
};

const formatName = (name: NameData) => {
  if (Object.keys(name).length === 0) {
    return '';
  }

  let fullName = '';

  if (name.First) {
    fullName += `${name.First} `;
  }
  if (name.Middle) {
    fullName += `${name.Middle} `;
  }
  if (name.Last) {
    fullName += `${name.Last} `;
  }

  return fullName.trim();
};

const formatFullName = (name: NameData, hasAge?: string, age?: string) => {
  let fullPrefixed = '';
  let fullName = formatName(name);

  fullName = hasAge !== '0' ? `${fullName}, ${age}` : fullName;
  fullPrefixed = name.Prefix ? `${name.Prefix} ${fullName}` : fullName;

  return { fullName, fullPrefixed };
};

const formatAddress = (address: AddressData) => {
  if (Object.keys(address).length === 0) {
    return;
  }

  let fullAddress = '';

  if (address.City) {
    const city = address.City.toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    fullAddress = city;
  }

  if (address.State) {
    fullAddress += `, ${address.State.toUpperCase()}`;
  }

  return fullAddress;
};
